
import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import { AttendeeInput, CalendarEvent, CalendarEventRegistration, CalendarEventRegistrationRequest, CalendarRegistrationForm, CoreLibService, Mutation, Query, WorksheetColumnType, CaptchaSolution, Captcha } from 'core-lib';
import  moment from 'moment';
import { ConfirmationService, MessageService } from 'primeng/api';

@Component({
    selector: 'lib-event-registration',
    templateUrl: './event-registration.component.html',
    styleUrls: ['./event-registration.component.scss'],
    animations: [
        trigger('fade', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate(200, style({ opacity: 1 }))
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate(200, style({ opacity: 0 }))
            ]),
        ])
    ],
    standalone: false
})
export class EventRegistrationComponent implements OnInit, OnChanges {

  @Input() event: CalendarEvent;
  @Output() closeEventRegistration = new EventEmitter<CalendarEvent>();
  @Output() pay = new EventEmitter<CalendarEvent>();

  //Animations
  fadeInFadeOut: Boolean = false;
  delayExec = ms => new Promise(res => setTimeout(res, ms));
  //Elements SignUp Form
  signUpForm: UntypedFormGroup;
  currentEventSignUps: CalendarEventRegistration[] = [];
  allPayed = true;
  showTicket: boolean[] = [];
  apiURL: string;
  requestInProgress: boolean = false;

  isPublicRegistration = false;
  publicRegistrationStatus: string = null;

  captchaControl: { code: string, captcha: Captcha };

  localStoragePublicRegistrationKey: string;

  cancelSubscriptionReason: string = null;

  extendedFieldEnumValues: { field?: string, values?: string[] } = {};
  registrationPaceholders: any = {
    "firstname": $localize`:@@event-registration.component.input.firstName.required:First name *`,
    "name": $localize`:@@event-registration.component.input.name.required:Last name *`,
    "email": $localize`:@@event-registration.component.input.email.notRequired:Email`,
    "phoneNumber": $localize`:@@event-registration.component.input.phoneNumber.notRequired:Phone number`,
    "comment": $localize`:@@event-registration.component.input.comment.notRequired:Comment`,
    "captcha": $localize`:@@event-registration.component.input.comment.captcha:Captch code`
  }

  requiredFieldValidators: any = {
    "firstname": true,
    "name": true,
    "email": false,
    "phoneNumber": false,
    "comment": false
  };
  //GraphQL Queries
  MUTATION_REQUEST_REGISTRATION = gql`
  mutation requestCalendarEventRegistration($request : CalendarEventRegistrationRequest){
    requestCalendarEventRegistration(request : $request){
      id
      clientId
      calendarEventId
      seat {
        id
        userProfileId
        name
        firstname
        phoneNumber
        email
        comment
      }
  }}`;


  MUTATION_REQUEST_PUBLIC_REGISTRATION = gql`
  mutation requestPublicCalendarEventRegistration($request : CalendarEventRegistrationRequest!,$captcha:CaptchaSolution!){
    requestPublicCalendarEventRegistration(request : $request,captcha:$captcha){
      id
      clientId
      calendarEventId
      seat {
        id
        userProfileId
        name
        firstname
        phoneNumber
        email
        comment
      }
  }}`;


  MUTATION_REQUEST_CANCEL_REGISTRATION = gql`
  mutation cancelCalendarEventRegistration($registrationId : ID!, $reason: String){
    cancelCalendarEventRegistration(registrationId : $registrationId, reason: $reason){
      id
      seriesId
      title
      subTitle
      dateStart
      dateEnd
      registrationRequired
      ownRegistrations {
        id
        payed
        seat{
          id
          userProfileId
          name
          firstname
          phoneNumber
          email
          comment
        }
      }
  }}`;

  QUERY_LOAD_EXTENDED_REGISTRATION_FORM = gql`
  query getCalendarRegistrationForm($id: ID!){
    getCalendarRegistrationForm(id : $id){
      id header footer fields{ columnName propertyName datatype mandatory}
    }
  }`;

  MUTATION_REQUEST_CAPTCHA = gql`
  mutation requestCaptcha($width: Int, $height: Int){
    requestCaptcha(width:$width, height:$height){
      id image
    }
  }`;

  QUERY_CHECK_CALENDAR_EVENT_REGISTRATION_STATUS = gql`
  query checkCalendarEventRegistrationStatus($registrationIds : [ID]! ){
    checkCalendarEventRegistrationStatus(registrationIds:$registrationIds)
  }`;



  constructor(private apolloClient: Apollo, @Inject("environment") public environment: any, private messageService: MessageService, public corelibService: CoreLibService, private confirmationService: ConfirmationService, @Inject('component') private component: string, private router: Router) { }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['event']) {
      this.initForm();
    }

    this.apiURL = this.environment.apiURL;
  }


  get publicRegistrationLocalStorageKey(): string {
    return `event-registrations-public-registration-state-${this.event.id}`;
  }

  get publicRegistrationIds(): string[] {
    const result = localStorage.getItem(this.publicRegistrationLocalStorageKey);
    if (result != null) {
      return JSON.parse(result);
    }
    return null;
  }

  set publicRegistrationIds(registrationIds: string[]) {
    localStorage.setItem(this.publicRegistrationLocalStorageKey, JSON.stringify(registrationIds));
  }

  get canAddAttendee(): boolean {
    return ((this.event.registrationCount != this.event.maxSignUps) || this.event.maxSignUps == null || this.event.waitingListEnabled)
    && (this.getattendees().length == 0 || this.event.multiSignUpAllowed == true
    && this.getattendees().length <= this.event.maxAdditionalSignUps)
    && !this.isDeadLineReached(this.event)
  }

  get canSubmitTowaitingList(): boolean {
    return  this.event.waitingListEnabled && (this.event.maxSignUps <= this.event.registrationCount) && this.getattendees().length > 0 && this.newAttendees().length > 0;
  }

  get canSubmit(): boolean {
    return ((this.event.maxSignUps > this.event.registrationCount) || this.event.maxSignUps == null) && this.getattendees().length > 0 && this.newAttendees().length > 0;
  }

  reservedUntil(registration: CalendarEventRegistration): Date {
    return moment(registration.creationDate).add('1', 'hour').toDate();
  }

  get paymentEnabled () : boolean{
    return this.event.products !=null && this.event.products.length > 0;
  }

  initForm(): void {
    if (this.event.registrationFormId) {
      this.apolloClient.query<Query>({
        query: this.QUERY_LOAD_EXTENDED_REGISTRATION_FORM,
        variables: {
          id: this.event.registrationFormId
        }
      }).subscribe((result) => {
        this.event.registrationForm = result.data.getCalendarRegistrationForm;
        if (this.event.registrationForm) {
          this.corelibService.hook('hook_appointment_management_registration_enum_values', { component: this.component, token: this.corelibService.getJWTToken(), departments: this.corelibService.getDepartments(), roles: this.corelibService.getRoles(), profile: this.corelibService.getSecurityInformation().userProfile }, null).then((values) => {
            this.extendedFieldEnumValues = values;
          });
          const registrations = this.event.ownRegistrations.map((registration) => this.generateAttendeeForm(registration, this.event.registrationForm));
          this.signUpForm = new UntypedFormGroup({
            "attendees": new UntypedFormArray(registrations)
          });
        }
      });
    } else {
      const registrations = this.event.ownRegistrations.map((registration) => this.generateAttendeeForm(registration, null));
      this.signUpForm = new UntypedFormGroup({
        "attendees": new UntypedFormArray(registrations)
      });
    }
    this.allPayed = this.event.ownRegistrations.filter((reg) => reg.status === 'RESERVED' && reg.payed == false).length == 0;
    this.isPublicRegistration = this.event.publicRegistrationAllowed && this.corelibService.getSecurityInformation().userProfile == null;
    if (this.isPublicRegistration) {
      this.registrationPaceholders.email = $localize`:@@event-registration.component.input.email.required:Email *`;
      this.requestNewCaptcha();
      const registrationIds = this.publicRegistrationIds;
      if (registrationIds){
        this.queryRegistrationStatus(registrationIds);
      }
    }
  }

  requestNewCaptcha(): void {
    this.apolloClient.query<Mutation>({
      query: this.MUTATION_REQUEST_CAPTCHA,
      variables: {
        height: 200,
        width: 200
      }
    }).subscribe((result) => {
      const captcha = result.data.requestCaptcha;
      this.captchaControl = { captcha: captcha, code: "" };
      this.captchaControl.captcha.image = `url("data:image/jpeg;base64,${captcha.image}")`;
    });
  }

  queryRegistrationStatus(registrationIds: string[]): void {
    this.apolloClient.query<Query>({
      query: this.QUERY_CHECK_CALENDAR_EVENT_REGISTRATION_STATUS,
      variables: {
        registrationIds
      }
    }).subscribe((result) => {
      if (!result.error) {
        const regs = result.data.checkCalendarEventRegistrationStatus.filter((e) => e != null);
        if (regs.length > 0) {
          this.publicRegistrationStatus = regs[0];
          if (this.publicRegistrationStatus === 'RESERVED' || this.publicRegistrationStatus === 'QUEUED'){
            setTimeout(()=>{
              this.queryRegistrationStatus(registrationIds);
            },5000)
          }
        }
      }
    });
  }

  /**
 *
 * @param appointment
 * @returns
 */
  isDeadLineReached(appointment: CalendarEvent): boolean {
    if (appointment.deadlineStartOffsetMinutes) {
      return moment().isAfter(moment(appointment.dateStart).subtract('minutes', appointment.deadlineStartOffsetMinutes));
    }
    return moment().isAfter(moment(appointment.dateStart))
  }

  /**
   * new form group
   * @param registration
   * @param registrationForm
   * @returns
   */
  generateAttendeeForm(registration: CalendarEventRegistration, registrationForm: CalendarRegistrationForm): UntypedFormGroup {

    let registrationId: string = registration?.id;
    let firstname: string = registration?.seat?.firstname;
    let name: string = registration?.seat?.name;
    let phoneNumber: string = registration?.seat?.phoneNumber;
    let email: string = registration?.seat?.email;
    let comment: string = registration?.seat?.comment;

    const formGroup = new UntypedFormGroup({
      "id": new UntypedFormControl(registrationId),
      "firstname": new UntypedFormControl({ value: firstname, disabled: registration?.id != null }, this.requiredValidatorsForField('firstname')),
      "name": new UntypedFormControl({ value: name, disabled: registration?.id != null }, this.requiredValidatorsForField('name')),
      "email": new UntypedFormControl({ value: email, disabled: registration?.id != null }, [Validators.email, ...this.requiredValidatorsForField('email')]),
      "phoneNumber": new UntypedFormControl({ value: phoneNumber, disabled: registration?.id != null }, this.requiredValidatorsForField('phoneNumber')),
      "comment": new UntypedFormControl({ value: comment, disabled: registration?.id != null }, this.requiredValidatorsForField('comment')),
    });

    if (registrationForm == null) {
      return formGroup;
    }

    const extendedFormFields = new UntypedFormGroup({});
    for (const field of registrationForm.fields) {
      let fieldValidators = [];
      if (field.mandatory) {
        if (field.datatype === WorksheetColumnType.Boolean) {
          fieldValidators = [Validators.requiredTrue];
        } else {
          fieldValidators = [Validators.required];
        }
      }
      let fieldValue = registration?.formData?.[field.propertyName];
      if (fieldValue == null && field.datatype === WorksheetColumnType.Boolean) {
        fieldValue = false;
      }
      extendedFormFields.addControl(field.propertyName, new UntypedFormControl({ value: fieldValue, disabled: registration?.id != null }, fieldValidators));
    }
    formGroup.addControl("formData", extendedFormFields);
    this.corelibService.hook('hook_appointment_management_registration_init_values', { component: this.component, token: this.corelibService.getJWTToken(), profile: this.corelibService.getSecurityInformation().userProfile }, null).then((values) => {
      formGroup.patchValue(values);
    });
    return formGroup;
  }


  ngOnInit(): void {
    this.fadeInFadeOut = true;
    const requiredFieldsHook = this.corelibService.hook('hook_appointment_management_registration_required_fields', { component: this.component }, null);

    requiredFieldsHook.then((result) => {
      if (result) {
        this.requiredFieldValidators = result;

        if (this.requiredFieldValidators.firstname) {
          this.registrationPaceholders.firstname = $localize`:@@event-registration.component.input.firstName.required:First name *`;
        } else {
          this.registrationPaceholders.firstname = $localize`:@@event-registration.component.input.firstName.notRequired:First name`;
        }

        if (this.requiredFieldValidators.name) {
          this.registrationPaceholders.name = $localize`:@@event-registration.component.input.name.required:Last Name *`;
        } else {
          this.registrationPaceholders.name = $localize`:@@event-registration.component.input.name.notRequired:Last Name`;
        }

        if (this.requiredFieldValidators.email) {
          this.registrationPaceholders.email = $localize`:@@event-registration.component.input.email.required:Email *`;
        } else {
          this.registrationPaceholders.email = $localize`:@@event-registration.component.input.email.notRequired:Email`;
        }

        if (this.requiredFieldValidators.phoneNumber) {
          this.registrationPaceholders.phoneNumber = $localize`:@@event-registration.component.input.phoneNumber.required:Phone number *`;
        } else {
          this.registrationPaceholders.phoneNumber = $localize`:@@event-registration.component.input.phoneNumber.notRequired:Phone number`;
        }

        if (this.requiredFieldValidators.comment) {
          this.registrationPaceholders.comment = $localize`:@@event-registration.component.input.comment.required:Comment *`;
        } else {
          this.registrationPaceholders.comment = $localize`:@@event-registration.component.input.comment.notRequired:Comment`;
        }
      }
      if (this.corelibService.getSecurityInformation().userProfile == null){
        this.requiredFieldValidators.email = true;
      }
    });

    this.event.ownRegistrations.forEach((currentObject, index) => {
      this.showTicket[index] = false;
    })

  }


  newAttendees(): AttendeeInput[] {
    let attendeeArr = this.signUpForm.controls['attendees'].value;
    return attendeeArr = attendeeArr.filter((signup) => signup.id == null);
  }

  onSubmitConfirmSignUp() {
      if(this.requestInProgress){
          return;
      }
      this.requestInProgress = true;

    let request = {
      calendarEventId: this.event.id,
    } as CalendarEventRegistrationRequest;

    let attendeeArr = this.newAttendees();
    request['attendees'] = attendeeArr as [AttendeeInput];
    request['attendees'].forEach(at => delete at['id']);

    if (this.isPublicRegistration) {
      this.apolloClient.mutate<Mutation>({
        mutation: this.MUTATION_REQUEST_PUBLIC_REGISTRATION,
        variables: {
          request: request,
          captcha: { id: this.captchaControl.captcha.id, content: this.captchaControl.code } as CaptchaSolution
        }
      }).subscribe((result) => {
        if (!result.errors) {
          this.initForm();
          const registrationIds = result.data.requestPublicCalendarEventRegistration.map((reg) => reg.id);
          this.publicRegistrationIds = registrationIds;
          this.queryRegistrationStatus(this.publicRegistrationIds);
          this.messageService.add({
            severity: 'info',
            life: 10000,
            summary: $localize`:@@event-registration.submitPublicRegistration: Registration submitted. We have sent you an email, please confirm your registration in the email.`,
          });
          this.requestInProgress = false;
        }
      });
    } else {
      this.apolloClient.mutate<Mutation>({
        mutation: this.MUTATION_REQUEST_REGISTRATION,
        variables: {
          request: request
        }
      }).subscribe((result) => {
        this.event.ownRegistrations = [...this.event.ownRegistrations, ...result.data.requestCalendarEventRegistration];
        this.onToastSubmit();
        this.initForm();
        this.closeEventRegistration.emit(this.event);
        this.requestInProgress = false;
      });
    }
  }


  requiredValidatorsForField(field): ValidatorFn[] {
    if (this.requiredFieldValidators[field] == true) {
      return [Validators.required];
    }
    return [];
  }
  /*
  * Form: Create Forms for MultiSignUp
  *
  */
  addAttendee() {
    if (!this.canAddAttendee){
      return;
    }
    const profile = this.corelibService.getSecurityInformation().userProfile;
    let firstname: string;
    let name: string;
    let email: string;
    let phonenUmber: string;
    if (this.getattendees().length == 0) {
      if (profile) {
        firstname = profile.firstname;
        name = profile.name;
        email = profile.email;
        phonenUmber = profile.phone ? profile.phone : profile.phoneMobile;
      }

      const tempReg = { seat: { firstname: firstname, name: name, email: email, phoneNumber: phonenUmber } } as CalendarEventRegistration;
      this.getattendees().push(this.generateAttendeeForm(tempReg, this.event.registrationForm));
    } else {
      this.getattendees().push(this.generateAttendeeForm(null, this.event.registrationForm));
    }

  }


  onDeleteAttendee(i) {
    const group = this.getattendees().get(`${i}`) as UntypedFormGroup;
    if (group.get('id').value == null) {
      this.getattendees().removeAt(i);
    } else {
      this.onClickCancelSignUp(i, group.get('id').value);
    }
  }

  getattendees(): UntypedFormArray {
    return this.signUpForm.controls['attendees'] as UntypedFormArray;
  }

  //Close Window
  async onCloseEventRegistration() {
    this.fadeInFadeOut = false;
    await this.delayExec(500); // To prevent the emit+closure of the Component
    this.closeEventRegistration.emit(this.event);
  }

  onClickCancelSignUp(index, registrationId: string) {
    this.confirmationService.confirm({
      key:'cancelSubscriptionDialog',
      message: $localize`:@@event-registration.cancelRegistration.messageService.message: Do you really want to cancel this registration?`,
      header: $localize`:@@event-registration.cancelRegistration.messageService.header: Cancel registration`,
      rejectLabel: $localize`:@@event-registration.cancelRegistration.messageService.rejectLabel: No`,
      acceptLabel: $localize`:@@event-registration.cancelRegistration.messageService.acceptLabel: Yes`,
      defaultFocus: 'none',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.onCancelSignUpConfirmed(index, registrationId)
      }
    });
  }

  onCancelSignUpConfirmed(index, registrationId: string) {
    this.apolloClient.mutate<Mutation>({
      mutation: this.MUTATION_REQUEST_CANCEL_REGISTRATION,
      variables: {
        reason: this.cancelSubscriptionReason,
        registrationId: registrationId
      }
    }).subscribe((result) => {
      this.onToastCancel();
      this.getattendees().removeAt(index);
      this.closeEventRegistration.emit(this.event);
    });
  }

  onToastSubmit() {
    this.messageService.add({
      severity: 'info',
      summary: $localize`:@@event-registration.submitRegistration.messageService.summary: Registration submitted`,
    });
  }

  onToastCancel() {
    this.messageService.add({
      severity: 'info',
      summary: $localize`:@@event-registration.cancelRegistration.messageService.summary: Registration cancelled`,
    });
  }

  onShowTicket(index: number) {
    this.showTicket[index] = !this.showTicket[index];
  }

}
